import { motion } from "framer-motion";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";

const SECTIONS = [
  {
    n: "01",
    title: "Who We Are",
    body: [
      'IN-it is operated by: In-Itapp, Lodjurstråket 1, 417 51 Gothenburg, Sweden. Email: info@in-itapp.com.',
      'For purposes of applicable data protection laws, including the General Data Protection Regulation ("GDPR"), In-Itapp is the data controller where applicable.',
    ],
  },
  {
    n: "02",
    title: "Our Privacy Principles",
    body: ["IN-it is built around live and temporary experiences."],
    list: [
      "Data minimization",
      "Privacy by design",
      "No sale of personal information",
      "No targeted advertising based on personal information",
      "No unnecessary retention of user information",
      "No sharing of personal information with third parties for their own advertising or marketing purposes",
    ],
    after: "Our goal is to process only the minimum information technically necessary to provide the Service.",
  },
  {
    n: "03",
    title: "Information We Process",
    body: [
      "IN-it does not seek to build permanent profiles of its users or collect personal information for advertising purposes. However, certain information may need to be processed temporarily in order for the Service to function.",
      "Live Audio and Video — When you participate in an IN-it live experience, your device may transmit live video and/or audio to other authorized participants. Unless a specific feature clearly tells you otherwise and you choose to use that feature, IN-it does not intentionally retain live audio or video after the live experience has ended.",
      "Camera and Microphone — IN-it may request access to your device's camera and microphone when required for live experiences. These permissions are used only to provide the functionality you choose to use. You can manage these permissions through your device settings.",
      "Location — If IN-it offers location-based functionality, access to your location will only occur with the appropriate device permission. Location information will only be used to provide the functionality requested by you and will not be sold or used for targeted advertising.",
      "Technical Information — Certain technical information may be processed temporarily as part of normal internet communications and to establish and maintain connections between your device and the Service. This may include information such as an IP address, device or connection information, and other information technically necessary to deliver the Service. We do not use this information to create advertising profiles or track users across unrelated apps or websites.",
    ],
  },
  {
    n: "04",
    title: "Accounts",
    body: [
      "If IN-it allows users to create accounts, we may need to process the minimum information necessary to create, authenticate, and maintain an account. Any such information will be limited to what is necessary to provide the requested account functionality.",
      "We will not sell account information or provide it to third parties for their own advertising or marketing purposes. Users may request deletion of their account and associated personal information where applicable.",
    ],
  },
  {
    n: "05",
    title: "How We Use Information",
    body: ["Where personal information must be processed to operate IN-it, we use it only as necessary to:"],
    list: [
      "Provide requested features and live experiences;",
      "Establish and maintain live connections;",
      "Authenticate users, if account functionality is provided;",
      "Protect the security and integrity of the Service;",
      "Comply with applicable legal obligations; and",
      "Respond to user requests.",
    ],
    after: "We do not use personal information for targeted advertising or to create commercial advertising profiles.",
  },
  {
    n: "06",
    title: "Data Storage and Retention",
    body: [
      "IN-it is designed to avoid unnecessary permanent storage of personal information. Information that must be processed temporarily to provide a live experience is intended to be retained only for as long as technically necessary to provide that functionality.",
      "If certain information must be retained for account functionality, security, legal compliance, or another clearly disclosed feature, it will be retained only for as long as reasonably necessary for that purpose. When information is no longer required, it will be deleted or anonymized as appropriate.",
    ],
  },
  {
    n: "07",
    title: "Sharing and Disclosure",
    body: [
      "IN-it does not sell personal information. We do not provide users' personal information to third parties for their own advertising, profiling, or marketing purposes.",
      "Information may be disclosed where required by applicable law, regulation, legal process, court order, or a valid governmental request.",
      "If IN-it later uses service providers that process personal information on our behalf, this Privacy Policy may be updated as appropriate, and such providers will be required to process information only for authorized purposes and subject to appropriate privacy and security protections as required by applicable law.",
    ],
  },
  {
    n: "08",
    title: "Advertising and Tracking",
    body: [
      "IN-it does not use personal information for targeted advertising. IN-it does not sell or share personal information for cross-context behavioral advertising. IN-it does not intentionally track users across third-party apps or websites for advertising purposes.",
    ],
  },
  {
    n: "09",
    title: "European Economic Area, United Kingdom and Switzerland",
    body: [
      'Users located in the European Economic Area ("EEA"), United Kingdom, or Switzerland may have privacy rights under applicable data protection laws, including the GDPR where applicable. Depending on the circumstances, these rights may include the right to:',
    ],
    list: [
      "Access personal information;",
      "Correct inaccurate personal information;",
      "Request deletion of personal information;",
      "Restrict or object to certain processing;",
      "Receive certain personal information in a portable format;",
      "Withdraw consent where processing is based on consent; and",
      "Lodge a complaint with the appropriate data protection authority.",
    ],
    after:
      "Because IN-it is designed to minimize the storage of personal information, in some circumstances we may not hold information that can be retrieved or associated with a particular user. Where personal information is processed, the applicable legal basis may include the performance of a contract, compliance with a legal obligation, legitimate interests where permitted by law, or your consent, depending on the particular processing activity. You may contact us at report@in-itapp.com regarding your privacy rights.",
  },
  {
    n: "10",
    title: "United States Privacy Rights",
    body: [
      "Residents of certain U.S. states may have additional rights under applicable state privacy laws. Depending on the applicable law and whether IN-it is subject to that law, these may include rights to: know or access personal information; request deletion; correct inaccurate personal information; obtain a portable copy of certain information; opt out of certain sales, sharing, targeted advertising, or profiling; and exercise privacy rights without unlawful discrimination.",
      "IN-it does not sell personal information and does not use personal information for targeted advertising.",
      "California Residents — California residents may have rights under the California Consumer Privacy Act (\"CCPA\"), as amended by the California Privacy Rights Act (\"CPRA\"), where those laws apply. IN-it does not sell personal information. IN-it does not share personal information for cross-context behavioral advertising. Requests concerning applicable California privacy rights may be submitted to report@in-itapp.com.",
    ],
  },
  {
    n: "11",
    title: "Children's Privacy",
    body: [
      "IN-it is not intended for children under the minimum age required to use the Service under applicable law. We do not knowingly collect personal information from children in violation of applicable law.",
      "If we learn that personal information from a child has been processed or retained in violation of applicable law, we will take appropriate steps to delete it. Parents or legal guardians who believe that a child has provided personal information in violation of this policy may contact us at report@in-itapp.com.",
    ],
  },
  {
    n: "12",
    title: "Security",
    body: [
      "We use reasonable technical and organizational safeguards designed to protect information processed through IN-it against unauthorized access, disclosure, alteration, or destruction.",
      "However, no method of transmission over the Internet, mobile network, or electronic storage system can be guaranteed to be completely secure. Accordingly, while we take reasonable measures to protect information, we cannot guarantee absolute security.",
    ],
  },
  {
    n: "13",
    title: "International Users",
    body: [
      "IN-it may be available to users in multiple countries. Where personal information is transferred internationally, we will handle such transfers in accordance with applicable data protection laws and implement appropriate safeguards where required.",
    ],
  },
  {
    n: "14",
    title: "Your Choices",
    body: [
      "You control whether IN-it can access device features such as your camera, microphone, and location through your device's privacy settings. You may revoke these permissions at any time through your device settings. Certain IN-it features may not function without the permissions necessary to provide them.",
      "Where account functionality is available, users may also contact us to exercise applicable rights concerning their account or personal information.",
    ],
  },
  {
    n: "15",
    title: "Changes to This Privacy Policy",
    body: [
      "This Privacy Policy may change in the future. IN-it is an evolving service, and we reserve the right to update, revise, modify, or replace this Privacy Policy from time to time to reflect changes to the Service, our technology, our information practices, our business operations, applicable laws or regulations, or for other legitimate operational purposes.",
      "The version of the Privacy Policy made available through the Service will indicate the date on which it was last updated. Where required by applicable law, we will provide appropriate notice of material changes and obtain consent where consent is legally required before the relevant changes take effect.",
      "Changes will become effective when stated in the updated Privacy Policy or as otherwise communicated to users, subject to applicable law. Your continued use of the Service after an updated Privacy Policy becomes effective may constitute acknowledgment of the updated policy where permitted by applicable law. Nothing in this section limits any privacy or data protection rights that cannot legally be waived or restricted.",
      "We encourage users to review this Privacy Policy periodically so they remain informed about our current privacy practices.",
    ],
  },
  {
    n: "16",
    title: "Contact Us",
    body: [
      "If you have questions about this Privacy Policy, IN-it's privacy practices, or your privacy rights, please contact: In-Itapp, Lodjurstråket 1, 417 51 Gothenburg, Sweden.",
      "General inquiries: info@in-itapp.com — Privacy and reporting: report@in-itapp.com",
    ],
  },
];

export default function PrivacyPage() {
  return (
    <main className="relative">
      <Navbar />
      <div data-testid="privacy-policy-container" className="max-w-4xl mx-auto px-6 pt-40 pb-24">
        <Link
          to="/"
          data-testid="privacy-back-link"
          className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.25em] text-zinc-500 hover:text-ink-coral transition-colors duration-200 mb-10"
        >
          <ArrowLeft className="w-4 h-4" /> BACK TO IN-ITAPP.COM
        </Link>

        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <div className="flex items-center gap-3 mb-6">
            <ShieldCheck className="w-6 h-6 text-ink-coral" />
            <span className="font-mono text-xs tracking-[0.3em] text-ink-coral">LEGAL</span>
          </div>
          <h1 className="font-display font-extrabold tracking-tighter text-white text-4xl sm:text-6xl mb-4">
            Privacy Policy for <span className="text-brand-gradient">IN-Itapp</span>
          </h1>
          <p className="font-mono text-xs tracking-[0.25em] text-zinc-500 mb-8">LAST UPDATED: SEPTEMBER 23, 2026</p>
          <div className="rounded-3xl border border-white/10 bg-ink-card/60 p-6 sm:p-8 mb-16">
            <p className="text-zinc-300 leading-relaxed mb-4">
              IN-it ("IN-it," "we," "us," or "our") respects your privacy and is committed to protecting it. This Privacy Policy explains how information is handled when you use the IN-it mobile application, website, and related services (collectively, the "Service").
            </p>
            <p className="text-zinc-300 leading-relaxed">
              Our approach is simple: IN-it is designed to minimize the collection and storage of personal data. We do not sell your personal information, use it for targeted advertising, or provide it to third parties for their own marketing purposes.
            </p>
          </div>
        </motion.div>

        <div className="space-y-14">
          {SECTIONS.map((s, i) => (
            <motion.section
              key={s.n}
              data-testid={`privacy-section-${s.n}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: Math.min(i * 0.03, 0.2) }}
              className="border-t border-white/10 pt-8"
            >
              <div className="flex items-baseline gap-5 mb-5">
                <span className="font-display font-extrabold text-3xl text-outline shrink-0">{s.n}</span>
                <h2 className="font-display font-bold text-white text-xl sm:text-2xl tracking-tight">{s.title}</h2>
              </div>
              <div className="space-y-4 text-zinc-400 leading-relaxed text-[15px]">
                {s.body.map((p, j) => (
                  <p key={j}>{p}</p>
                ))}
                {s.list && (
                  <ul className="list-none space-y-2 pl-1">
                    {s.list.map((item, j) => (
                      <li key={j} className="flex items-start gap-3">
                        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-ink-coral shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {s.after && <p>{s.after}</p>}
              </div>
            </motion.section>
          ))}
        </div>

        <p className="mt-16 font-mono text-xs tracking-[0.2em] text-zinc-400">© 2026 IN-ITAPP. ALL RIGHTS RESERVED.</p>
      </div>
      <Footer />
    </main>
  );
}
